import React, { createContext, useEffect, useContext, useState, useMemo, useCallback } from 'react';
import { Provider, useSelector, useDispatch } from 'react-redux';
import mpa from '@mpfe/mpa';
import { Router } from 'react-router-dom';
import { useLocation, useHistory } from 'react-router';

var DataProviderContext = /*#__PURE__*/createContext({});
DataProviderContext.displayName = 'DataProviderContext';

function createModels(models, dataProvider) {
  return models.map(function (model) {
    return model(dataProvider);
  });
}

var coreStore = /*#__PURE__*/mpa({
  silence: true
});

var CorePortalContext = function CorePortalContext(_ref) {
  var dataProvider = _ref.dataProvider,
      children = _ref.children,
      _ref$models = _ref.models,
      models = _ref$models === void 0 ? [] : _ref$models,
      store = _ref.store;
  coreStore.useModel.apply(coreStore, createModels(models, dataProvider));
  coreStore.run();
  return React.createElement(Provider, {
    store: store || coreStore.store
  }, React.createElement(DataProviderContext.Provider, {
    value: dataProvider
  }, children));
};

var CorePortalRouter = function CorePortalRouter(_ref) {
  var children = _ref.children,
      history = _ref.history;
  return React.createElement(Router, {
    history: history
  }, children);
};

var PortalSite = function PortalSite(_ref) {
  var children = _ref.children,
      dataProvider = _ref.dataProvider,
      _ref$models = _ref.models,
      models = _ref$models === void 0 ? [] : _ref$models,
      history = _ref.history,
      root = _ref.root,
      store = _ref.store;
  useEffect(function () {
    coreStore.store.dispatch(coreStore.actions('routesMap').getRoutes(root));
  }, [root]);
  return React.createElement(CorePortalContext, {
    store: store,
    models: models,
    dataProvider: dataProvider
  }, React.createElement(CorePortalRouter, {
    history: history
  }, children));
};

function useDataProvider(method) {
  var dataProvider = useContext(DataProviderContext);
  return dataProvider[method];
}

var useQuery = function useQuery(_ref) {
  var api = _ref.api,
      options = _ref.options,
      formatResult = _ref.formatResult,
      onSuccess = _ref.onSuccess,
      onError = _ref.onError;
  var dataProvider = useDataProvider(api);

  if (dataProvider === undefined) {
    throw new Error('No dataProvider found!');
  }

  var _useState = useState(false),
      loading = _useState[0],
      setLoading = _useState[1];

  var _useState2 = useState(null),
      error = _useState2[0],
      setError = _useState2[1];

  var _useState3 = useState(),
      data = _useState3[0],
      setData = _useState3[1];

  useEffect(function () {
    setLoading(true);
    dataProvider(options).then(function (data) {
      var result = typeof formatResult === 'function' ? formatResult(data) : data;
      setData(result);
      onSuccess && onSuccess(data, options);
    })["catch"](function (err) {
      setError(err);
      onError && onError(err, options);
    })["finally"](function () {
      setLoading(false);
    }); // todo cancelable
    // return () => {
    // }
  }, [dataProvider, onError, onSuccess, options, formatResult]);
  return {
    loading: loading,
    response: data,
    error: error
  };
};

var useQueryList = function useQueryList(_ref) {
  var _response$records, _response$pages, _response$page;

  var resource = _ref.resource,
      pagination = _ref.pagination,
      onError = _ref.onError,
      onSuccess = _ref.onSuccess,
      formatResult = _ref.formatResult,
      params = _ref.params;
  var memoOptions = useMemo(function () {
    return {
      resource: resource,
      size: pagination.size,
      page: pagination.page,
      params: params
    };
  }, [resource, pagination.size, pagination.page, params]);

  var _useQuery = useQuery({
    api: 'queryList',
    options: memoOptions,
    formatResult: formatResult,
    onSuccess: onSuccess,
    onError: onError
  }),
      response = _useQuery.response,
      error = _useQuery.error,
      loading = _useQuery.loading;

  return {
    records: (_response$records = response === null || response === void 0 ? void 0 : response.records) !== null && _response$records !== void 0 ? _response$records : [],
    error: error,
    loading: loading,
    pages: (_response$pages = response === null || response === void 0 ? void 0 : response.pages) !== null && _response$pages !== void 0 ? _response$pages : 0,
    total: response === null || response === void 0 ? void 0 : response.total,
    page: (_response$page = response === null || response === void 0 ? void 0 : response.page) !== null && _response$page !== void 0 ? _response$page : 0
  };
};

var useRoutesMap = function useRoutesMap() {
  var routesMap = useSelector(function (state) {
    return state.routesMap.root;
  });
  return routesMap;
};

var useQueryOne = function useQueryOne(params) {
  var _response$data;

  var memoOptions = useMemo(function () {
    return {
      resource: params.resource,
      params: params.params,
      formatResult: params.formatResult,
      onSuccess: params.onSuccess,
      onError: params.onError
    };
  }, [params.resource, params.params, params.formatResult, params.onSuccess, params.onError]);

  var _useQuery = useQuery({
    api: "queryOneById",
    options: memoOptions
  }),
      response = _useQuery.response,
      loading = _useQuery.loading,
      error = _useQuery.error;

  return {
    data: (_response$data = response === null || response === void 0 ? void 0 : response.data) !== null && _response$data !== void 0 ? _response$data : {},
    loading: loading,
    error: error
  };
};

var useHelmet = function useHelmet(_ref) {
  var title = _ref.title,
      description = _ref.description,
      keywords = _ref.keywords;
  var dispatch = useDispatch();
  var setHelmet = useCallback(function () {
    return dispatch(coreStore.actions("helmet").setHelmet({
      payload: {
        title: title,
        description: description,
        keywords: keywords
      }
    }));
  }, [dispatch, title, description, keywords]);
  useEffect(function () {
    setHelmet();
  }, [setHelmet]);
};

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

var noop = function noop() {};

var useListController = function useListController(_ref) {
  var resource = _ref.resource,
      _ref$size = _ref.size,
      size = _ref$size === void 0 ? 3 : _ref$size,
      _ref$onListChange = _ref.onListChange,
      onListChange = _ref$onListChange === void 0 ? noop : _ref$onListChange,
      _ref$currentPage = _ref.currentPage,
      currentPage = _ref$currentPage === void 0 ? 1 : _ref$currentPage;
  var location = useLocation();
  var history = useHistory();
  var params = new URLSearchParams(location.search);
  var pageParam = params.get('page') || currentPage;

  var _useState = useState(+pageParam),
      page = _useState[0],
      setPage = _useState[1];

  var data = useQueryList({
    pagination: {
      page: page,
      size: size
    },
    resource: resource
  });

  var onPageChange = function onPageChange(page) {
    setPage(page);
    onListChange();
    params.set('page', page + '');
    history.push({
      pathname: location.pathname,
      search: '?' + params.toString()
    });
  };

  return _extends({
    onPageChange: onPageChange
  }, data);
};

export { CorePortalContext, CorePortalRouter, PortalSite, coreStore, createModels, useDataProvider, useHelmet, useListController, useQuery, useQueryList, useQueryOne, useRoutesMap };
//# sourceMappingURL=core.esm.js.map
